import { RefreshCw, RotateCw, Search } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../components/button";
import PageHeader from "../../../components/common/PageHeader";
import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import { DialogV2 } from "../../../components/dialog-v2";
import { Input } from "../../../components/input";
import { MultiSelectV2 } from "../../../components/multi-select-v2";
import { useToast } from "../../../components/toast-container";
import { MerchantBrandsTable } from "../components/MerchantBrandsTable";
import { getMerchantBrandAdmin } from "../api/merchantBrandAdmin.api";
import { useMerchantBrandCatalog } from "../hooks/useMerchantBrandCatalog";
import { useMerchantBrandCategoryOptions } from "../hooks/useMerchantBrandCategoryOptions";
import { useSyncMerchantBrands } from "../hooks/useSyncMerchantBrands";
import type {
  MerchantBrandAdmin,
  MerchantBrandCatalogListItem,
} from "../types/merchantBrandAdmin.types";

type FormState = {
  categoryIds: string[];
};

const initialForm: FormState = {
  categoryIds: [],
};

export function MerchantBrandCatalogPage() {
  const catalog = useMerchantBrandCatalog();
  const [isSyncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<MerchantBrandAdmin | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(initialForm);
  const [actionError, setActionError] = useState<string | null>(null);
  const [brandPendingActivationChange, setBrandPendingActivationChange] =
    useState<MerchantBrandCatalogListItem | null>(null);
  const [isUpdatingActivation, setIsUpdatingActivation] = useState(false);
  const { addToast } = useToast();
  const { loading: isSyncing, sync } = useSyncMerchantBrands();
  const categoryOptions = useMerchantBrandCategoryOptions(isDialogOpen);

  const refreshAll = () => catalog.refetch();

  const openEdit = async (brandId: string) => {
    try {
      const brand = await getMerchantBrandAdmin(brandId);
      setEditing(brand);
      setForm({
        categoryIds: brand.merchant_category_ids,
      });
      setActionError(null);
      setDialogOpen(true);
    } catch (requestError) {
      addToast({
        type: "error",
        title: "Unable to load merchant brand",
        message: requestError instanceof Error ? requestError.message : "Please try again.",
      });
    }
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;

    try {
      await catalog.saveCategories(editing.id, form.categoryIds);
      setDialogOpen(false);
    } catch (requestError) {
      setActionError(
        requestError instanceof Error ? requestError.message : "Unable to save merchant brand",
      );
    }
  };

  const confirmActivationChange = async () => {
    if (!brandPendingActivationChange) return;

    setIsUpdatingActivation(true);
    try {
      const isActive = !brandPendingActivationChange.is_active;
      await catalog.updateStatus(brandPendingActivationChange.id, isActive);
      addToast({
        type: "success",
        title: `Merchant brand ${isActive ? "enabled" : "disabled"}`,
        message: isActive
          ? `${brandPendingActivationChange.brand_name} is available in merchant brand options again.`
          : `${brandPendingActivationChange.brand_name} will no longer appear in merchant brand options.`,
      });
      setBrandPendingActivationChange(null);
    } catch (requestError) {
      addToast({
        type: "error",
        title: "Unable to update merchant brand visibility",
        message: requestError instanceof Error ? requestError.message : "Please try again.",
      });
    } finally {
      setIsUpdatingActivation(false);
    }
  };

  const handleSync = async () => {
    try {
      const result = await sync();
      setSyncConfirmOpen(false);
      await catalog.refetch();
      addToast({
        type: "success",
        title: "Tango catalog synchronized",
        message: `Brands: ${result.brands_created} created, ${result.brands_updated} updated, ${result.brands_deleted} archived. Items: ${result.items_created} created, ${result.items_updated} updated, ${result.items_deleted} archived.`,
      });
    } catch (requestError) {
      addToast({
        type: "error",
        title: "Sync failed",
        message:
          requestError instanceof Error
            ? requestError.message
            : "Unable to synchronize Tango catalog.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <PageHeader
        title="Merchant Brands"
        subtitle="Manage merchant brands and review their local catalog items."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => void refreshAll()}
              disabled={catalog.isLoading || isSyncing}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${catalog.isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => setSyncConfirmOpen(true)}
              disabled={isSyncing}
            >
              <RotateCw className={`mr-2 h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
              Sync Tango Catalog
            </Button>
          </div>
        }
      />
      <section className="mt-6 rounded-xl border border-[#DCDCDC] bg-white p-4 shadow-sm">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-[#1D1D1F]">Merchant Brand Catalog</h2>
          <p className="mt-1 text-sm text-[#75757D]">
            Review local brands against the current Tango catalog.
          </p>
        </div>
        <div className="mb-4 max-w-md">
          <Input
            value={catalog.keyword}
            onChange={(event) => catalog.setKeyword(event.target.value)}
            placeholder="Search merchant brand"
            startIcon={<Search className="h-4 w-4 text-gray-400" />}
          />
        </div>
        <MerchantBrandsTable
          brands={catalog.items}
          loading={catalog.isLoading}
          error={catalog.error}
          onRetry={() => void catalog.refetch()}
          onUpdateCategories={(brandId) => void openEdit(brandId)}
          onToggleActive={setBrandPendingActivationChange}
        />
      </section>
      <DialogV2
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Update Merchant Categories"
        maxWidth="md"
        actions={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              form="merchant-brand-form"
              type="submit"
              disabled={categoryOptions.isLoading || Boolean(categoryOptions.error)}
            >
              Update categories
            </Button>
          </div>
        }
      >
        <form
          id="merchant-brand-form"
          onSubmit={submit}
          className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2"
        >
          <label className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
            <span>Merchant categories</span>
            <MultiSelectV2
              value={form.categoryIds}
              onChange={(categoryIds) => setForm({ categoryIds })}
              options={categoryOptions.options}
              placeholder={
                categoryOptions.isLoading ? "Loading categories..." : "Select categories"
              }
              searchPlaceholder="Search merchant categories"
              emptyMessage="No merchant categories found."
              disabled={categoryOptions.isLoading}
              onClear={() => setForm({ categoryIds: [] })}
            />
          </label>
          {categoryOptions.error ? (
            <p className="text-sm text-red-600 md:col-span-2">{categoryOptions.error}</p>
          ) : null}
          {actionError ? <p className="text-sm text-red-600 md:col-span-2">{actionError}</p> : null}
        </form>
      </DialogV2>
      <ConfirmationDialog
        isOpen={isSyncConfirmOpen}
        onClose={() => setSyncConfirmOpen(false)}
        onConfirm={() => void handleSync()}
        title="Sync Tango merchant catalog?"
        description="Records no longer returned by Tango will be archived locally. Existing coupon links are preserved."
        confirmText="Sync catalog"
        isLoading={isSyncing}
        type="warning"
      />
      <ConfirmationDialog
        isOpen={brandPendingActivationChange !== null}
        onClose={() => setBrandPendingActivationChange(null)}
        onConfirm={() => void confirmActivationChange()}
        title={`${brandPendingActivationChange?.is_active ? "Disable" : "Enable"} merchant brand?`}
        description={
          brandPendingActivationChange?.is_active
            ? `Disable ${brandPendingActivationChange.brand_name}? It will no longer appear in merchant brand options.`
            : `Enable ${brandPendingActivationChange?.brand_name ?? "this merchant brand"}? It will appear in merchant brand options again.`
        }
        confirmText={brandPendingActivationChange?.is_active ? "Disable brand" : "Enable brand"}
        isLoading={isUpdatingActivation}
        type={brandPendingActivationChange?.is_active ? "warning" : "info"}
      />
    </div>
  );
}
